import React, { useState, useEffect } from 'react';
import {
  Card, Table, Typography, Button, Tag, Space, Input, Select,
  message, Popconfirm, Modal, Image, Tooltip
} from 'antd';
import {
  StarOutlined, StarFilled, CopyOutlined,
  DeleteOutlined, EyeOutlined
} from '@ant-design/icons';
import { useAuth } from '../utils/auth';
import aiContentService from '../services/aiContentService';
import moment from 'moment';

const { Text } = Typography;
const { Option } = Select;

const CONTENT_TYPES = {
  PRODUCT_PROMO: { label: '带货种草', icon: '🛍️', color: 'red' },
  ENGAGEMENT: { label: '互动福利', icon: '🎁', color: 'green' },
  BRAND: { label: '品牌故事', icon: '💡', color: 'blue' },
  DAILY: { label: '日常问候', icon: '☀️', color: 'orange' },
  HOLIDAY: { label: '节日祝福', icon: '🎉', color: 'purple' },
};

const AiContentHistory = () => {
  const { tenant } = useAuth();
  const tenantId = tenant?.id;

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState(null);
  const [favoriteOnly, setFavoriteOnly] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1, pageSize: 20, total: 0,
    showSizeChanger: true, showQuickJumper: true,
    showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
  });

  const fetchHistory = async (page = 1, pageSize = 20) => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const params = { tenantId, page: page - 1, size: pageSize };
      if (typeFilter) params.contentType = typeFilter;
      if (favoriteOnly) params.favoriteOnly = true;
      const response = await aiContentService.getHistory(params);
      if (response.success) {
        setHistory(response.data.content || []);
        setPagination(prev => ({ ...prev, current: page, pageSize, total: response.data.totalElements || 0 }));
      } else {
        message.error(response.message || '获取历史记录失败');
      }
    } catch (error) {
      message.error('获取历史记录失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHistory(); }, [tenantId, typeFilter, favoriteOnly]);

  const handleToggleFavorite = async (record) => {
    try {
      const response = await aiContentService.toggleFavorite(record.id);
      if (response.success) {
        message.success(record.isFavorite ? '已取消收藏' : '已收藏');
        fetchHistory(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || '操作失败');
      }
    } catch (error) { message.error('操作失败'); }
  };

  const handleDelete = async (id) => {
    try {
      const response = await aiContentService.deleteHistory(id);
      if (response.success) { message.success('删除成功'); fetchHistory(pagination.current, pagination.pageSize); }
      else message.error(response.message || '删除失败');
    } catch (error) { message.error('删除失败'); }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => message.success('已复制到剪贴板'));
  };

  const handleViewDetail = (record) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const columns = [
    {
      title: '类型', dataIndex: 'contentType', key: 'contentType', width: 110,
      render: (type) => {
        const config = CONTENT_TYPES[type] || {};
        return <Tag color={config.color}>{config.icon} {config.label || type}</Tag>;
      }
    },
    {
      title: '生成内容', dataIndex: 'generatedText', key: 'generatedText', ellipsis: true,
      render: (text) => <Tooltip title={text}><span>{(text || '').substring(0, 80)}...</span></Tooltip>
    },
    {
      title: '商品', dataIndex: 'productName', key: 'productName', width: 120,
      render: (name) => name || '-'
    },
    {
      title: '活动', dataIndex: 'campaignName', key: 'campaignName', width: 120,
      render: (name) => name || '-'
    },
    {
      title: '模型', key: 'model', width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag style={{ fontSize: 10 }}>{record.provider}</Tag>
          {record.model && <Text type="secondary" style={{ fontSize: 10 }}>{record.model}</Text>}
        </Space>
      )
    },
    {
      title: '时间', dataIndex: 'createdAt', key: 'createdAt', width: 160,
      render: (time) => time ? moment(time).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '操作', key: 'action', width: 200, fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>查看</Button>
          <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => handleCopy(record.generatedText)}>复制</Button>
          <Button type="link" size="small"
            icon={record.isFavorite ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
            onClick={() => handleToggleFavorite(record)}>
            {record.isFavorite ? '取消' : '收藏'}
          </Button>
          <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    },
  ];

  return (
    <div>
      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Select placeholder="按类型筛选" allowClear style={{ width: 150 }} onChange={setTypeFilter}>
            {Object.entries(CONTENT_TYPES).map(([key, val]) => <Option key={key} value={key}>{val.icon} {val.label}</Option>)}
          </Select>
          <Button type={favoriteOnly ? 'primary' : 'default'}
            icon={favoriteOnly ? <StarFilled /> : <StarOutlined />}
            onClick={() => setFavoriteOnly(!favoriteOnly)}>
            {favoriteOnly ? '查看全部' : '仅看收藏'}
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={history}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={(pag) => fetchHistory(pag.current, pag.pageSize)}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="内容详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="copy" icon={<CopyOutlined />} onClick={() => handleCopy(selectedRecord?.generatedText)}>复制文案</Button>,
          <Button key="close" onClick={() => setDetailModalVisible(false)}>关闭</Button>,
        ]}
        width={700}
      >
        {selectedRecord && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <Space>
                <Tag color={CONTENT_TYPES[selectedRecord.contentType]?.color}>
                  {CONTENT_TYPES[selectedRecord.contentType]?.icon} {CONTENT_TYPES[selectedRecord.contentType]?.label}
                </Tag>
                <Tag>{selectedRecord.provider}</Tag>
                {selectedRecord.model && <Tag color="blue">{selectedRecord.model}</Tag>}
                {selectedRecord.tokensUsed && <Text type="secondary" style={{ fontSize: 12 }}>Token: {selectedRecord.tokensUsed}</Text>}
              </Space>
            </div>
            {selectedRecord.productName && <div style={{ marginBottom: 8 }}>商品：<Tag>{selectedRecord.productName}</Tag></div>}
            {selectedRecord.campaignName && <div style={{ marginBottom: 8 }}>活动：<Tag>{selectedRecord.campaignName}</Tag></div>}
            <div style={{ padding: 16, background: '#f6f8fa', borderRadius: 8, whiteSpace: 'pre-wrap', lineHeight: 1.8, marginBottom: 16 }}>
              {selectedRecord.generatedText}
            </div>
            {selectedRecord.images && selectedRecord.images.length > 0 && (
              <div>
                <Text strong>配图</Text>
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  {selectedRecord.images.map((img, i) => (
                    <Image key={i} src={img.ossUrl || img.url} width={100} height={100} style={{ objectFit: 'cover', borderRadius: 8 }} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AiContentHistory;
